// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { qtyFieldStyles } from "@ces/App/Components/Qty/QtyField";
import { FormFieldLabel } from "@ces/MobiusOverrides/FormField";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, xScreen } from "@ces/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

qtyFieldStyles.actionButton = {
    css: css`
        height: 29px;
        padding: 12px 8px;
        display: flex;
        align-items: center;
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        border: none;
        background-color: ${color("common.ui1")};
        &:disabled {
            opacity: 0.33;
        }
        ${IconWrapper} {
            color: ${color("text.link")};
            &,
            svg {
                width: 10px;
                height: 10px;
            }
        }
        &:hover:not(:disabled),
        &:focus {
            ${IconWrapper} {
                color: ${color("common.ui8")};
            }
        }
    `,
};

qtyFieldStyles.wrapper = {
    css: css`
        background: ${color("common.ui1")};
        display: flex;
        align-items: center;
        width: 116px;
        min-width: 116px;
        height: 33px;
        border-radius: 2px;
        overflow: hidden;
        &:not(${FormFieldLabel}) {
            border: 1px solid ${color("common.border")};

            &:hover {
                border-color: ${color("common.ui8")};
            }

            &:focus-within {
                border-color: ${color("primary")};
            }
        }
        overflow-x: hidden;
        &&& input:focus {
            padding: 0;
        }
    `,
};

qtyFieldStyles.editableQuantityTextField = {
    sizeVariant: "small",
    mergeCss: true,
    cssOverrides: {
        formField: css`
            ${TypographyStyle} {
                text-align: center;
            }

            &&& {
                input {
                    &,
                    &:hover:not(:disabled),
                    &:focus:not(:disabled) {
                        border: none;
                        border-radius: 0;
                        outline: none;
                        box-shadow: none;
                    }
                }
            }
        `,
        formInputWrapper: css`
            &,
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                border: none;
                box-shadow: none;
                padding: 0;
            }
        `,
        inputSelect: css`
            font-size: 14px;
            line-height: 16px;
            text-align: center;
            height: 29px;
            padding: 0;
        `,
    },
};

qtyFieldStyles.container = {
    css: css`
        display: flex;
        justify-content: center;
        flex-direction: column;
    `,
};

qtyFieldStyles.questionIconStyles = {
    size: 14,
    css: css`
        opacity: 0.66;
        color: ${color("common.ui7")};
        &:hover {
            opacity: 1;
        }
    `,
};

qtyFieldStyles.multipleSaleQtyToolTip = {
    cssOverrides: {
        tooltipClickable: css`
            margin: 0;
        `,
        tooltipBody: css`
            line-height: 19px;
        `,
    },
};

qtyFieldStyles.qtyLabelNotEditable = {
    as: "label",
    size: 12,
    weight: fontConfig.weight.regular,
    lineHeight: "13px",
    css: css`
        margin-bottom: 8px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        @media print {
            &&& {
                font-size: 14px;
                line-height: 16px;
            }
        }
    `,
};

qtyFieldStyles.valueText = {
    weight: fontConfig.weight.bold,
    css: css`
        display: flex;
        gap: 4px;
        align-items: center;
        line-height: 19px;
        @media print {
            font-size: 16px;
        }
    `,
};

qtyFieldStyles.qtyLabel = {
    size: 12,
    lineHeight: "13px",
    weight: fontConfig.weight.regular,
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            margin-bottom: 8px;
            display: block;
        }
    `,
};

qtyFieldStyles.qtyLabelContainer = {
    css: css`
        display: flex;
        align-items: center;
        gap: 5px;
    `,
};
qtyFieldStyles.tooltipBody = {
    css: css`
        display: flex;
        gap: 8px;
        margin-top: 8px;
    `,
};
qtyFieldStyles.tooltipText = {
    size: 14,
    lineHeight: "22px",
};
qtyFieldStyles.headingAndTextStyles = {
    wrapper: {
        css: css`
            flex-direction: row;
            width: auto;
            gap: 5px;
            flex-wrap: wrap;
        `,
    },
    heading: {
        size: 12,
        lineHeight: "13px",
        weight: fontConfig.weight.bold,
        css: css`
            text-transform: none;
        `,
    },
    text: {
        size: 12,
        lineHeight: "13px",
        css: css`
            width: auto;
        `,
    },
};
